import { useCallback } from "react";
import { createRoute, useNavigate, useParams } from "@tanstack/react-router";
import type { z } from "zod";
import { SourcesStatus, SourcesView, sourcesRouteSearchSchema, type SourcesSearch } from "@amber/collection/ui";
import { AppFrame } from "../shell/AppFrame";
import { usePageTitle } from "../shell/title";
import { rootRoute } from "./root";

type Next = SourcesSearch & { add?: "1" | undefined };

function SourcesRoute({
  search,
  sourceId,
  gapId = null,
}: {
  search: z.infer<typeof sourcesRouteSearchSchema>;
  sourceId: string | null;
  gapId?: string | null;
}) {
  usePageTitle("Sources");
  const navigate = useNavigate();
  const { add, ...filters } = search;
  const go = useCallback(
    (id: string | null, next: Next) => {
      if (id) void navigate({ to: "/sources/$sourceId", params: { sourceId: id }, search: next });
      else if (gapId && (next.tab ?? "gaps") === "gaps")
        void navigate({ to: "/sources/gaps/$gapId", params: { gapId }, search: next });
      else void navigate({ to: "/sources", search: next });
    },
    [navigate, gapId],
  );
  return (
    <AppFrame view="Sources" status={<SourcesStatus />}>
      {() => (
        <SourcesView
          search={filters}
          sourceId={sourceId}
          gapId={gapId}
          adding={add === "1"}
          onSearch={(next) => {
            go(sourceId, next);
          }}
          onOpenSource={(id, next) => {
            go(id, next ?? filters);
          }}
          onAdd={(open) => {
            go(sourceId, { ...filters, ...(open ? { add: "1" as const } : {}) });
          }}
        />
      )}
    </AppFrame>
  );
}

// DI-114: the drawer and gap routes are children so the list stays mounted and keeps its checked rows, chosen
// columns and expanded groups while a drawer opens and closes.
export const sourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sources",
  validateSearch: sourcesRouteSearchSchema,
  component: function SourcesListRoute() {
    const { sourceId, gapId } = useParams({ strict: false });
    return <SourcesRoute search={sourcesRoute.useSearch()} sourceId={sourceId ?? null} gapId={gapId ?? null} />;
  },
});

export const sourceRoute = createRoute({
  getParentRoute: () => sourcesRoute,
  path: "$sourceId",
  component: () => null,
});

export const gapRoute = createRoute({
  getParentRoute: () => sourcesRoute,
  path: "gaps/$gapId",
  component: () => null,
});
